import { useState } from "react";
import { useAddNewBusinessdetailMutation } from "./businessdetailsApiSlice";
import { useNavigate } from "react-router-dom";

export default function NewBusinessdetailForm() {
  const navigate = useNavigate();
  const [addNewBusinessdetail, { isLoading, isError, error }] = useAddNewBusinessdetailMutation();

  // Main fields
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("open");

  // Extended fields
  const [entrepreneur, setEntrepreneur] = useState("");
  const [entrepreneurName, setEntrepreneurName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [projectImpactCategory, setProjectImpactCategory] = useState("");
  const [submissionDate, setSubmissionDate] = useState("");

  // Finance
  const [funding_request, setFundingRequest] = useState(0);
  const [funding_request_opex, setFundingRequestOpex] = useState(0);
  const [funding_request_capex, setFundingRequestCapex] = useState(0);

  // Founders (array of objects)
  const [founders, setFounders] = useState([{ name: "", expertise: "" }]);

  // Business Model Canvas 9 blocks (arrays of strings)
  const [keyPartners, setKeyPartners] = useState([""]);
  const [keyActivities, setKeyActivities] = useState([""]);
  const [keyResources, setKeyResources] = useState([""]);
  const [valuePropositions, setValuePropositions] = useState([""]);
  const [customerRelationships, setCustomerRelationships] = useState([""]);
  const [channels, setChannels] = useState([""]);
  const [customerSegments, setCustomerSegments] = useState([""]);
  const [costStructure, setCostStructure] = useState([""]);
  const [revenueStreams, setRevenueStreams] = useState([""]);

  // Validation (basic)
  const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
  const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
  const canSave = validTitle && validShort && !isLoading;

  // Helper for array fields
  const handleArrayChange = (setter, arr, idx, value) => {
    const newArr = [...arr];
    newArr[idx] = value;
    setter(newArr);
  };
  const handleAddArrayItem = (setter, arr) => setter([...arr, ""]);
  const handleRemoveArrayItem = (setter, arr, idx) => setter(arr.filter((_, i) => i !== idx));

  // Founders helpers
  const handleFounderChange = (idx, field, value) => {
    const newArr = [...founders];
    newArr[idx][field] = value;
    setFounders(newArr);
  };
  const handleAddFounder = () => setFounders([...founders, { name: "", expertise: "" }]);
  const handleRemoveFounder = (idx) => setFounders(founders.filter((_, i) => i !== idx));

  const onSaveBusinessdetailClicked = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    try {
      await addNewBusinessdetail({
        title,
        shortTitle,
        description, // <-- add this line
        status,
        entrepreneur,
        entrepreneurName,
        organizationName,
        projectImpactCategory,
        submissionDate: submissionDate ? new Date(submissionDate) : undefined,
        finance: {
          funding_request: Number(funding_request),
          funding_request_opex: Number(funding_request_opex),
          funding_request_capex: Number(funding_request_capex),
        },
        founder: founders.filter(f => f.name || f.expertise),
        components: {
          keyPartners: keyPartners.filter(Boolean),
          keyActivities: keyActivities.filter(Boolean),
          keyResources: keyResources.filter(Boolean),
          valuePropositions: valuePropositions.filter(Boolean),
          customerRelationships: customerRelationships.filter(Boolean),
          channels: channels.filter(Boolean),
          customerSegments: customerSegments.filter(Boolean),
          costStructure: costStructure.filter(Boolean),
          revenueStreams: revenueStreams.filter(Boolean),
        },
      }).unwrap();
      navigate("/dash/businessdetails");
    } catch {
      /* handled by error banner */
    }
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6"
    >
      {/* Error banner */}
      {isError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {error?.data?.message || "Something went wrong."}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validTitle ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Main Businessdetail Title"
          />
          {!validTitle && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–80 characters.</p>
          )}
        </div>

        {/* Short title */}
        <div>
          <label htmlFor="shorttitle" className="block text-sm font-medium text-gray-700">
            Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
          </label>
          <input
            id="shorttitle"
            type="text"
            value={shortTitle}
            onChange={(e) => setShortTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validShort ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Short name"
          />
          {!validShort && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–60 characters.</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description <span className="text-gray-400 font-normal">(optional, multiline)</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5"
            placeholder="Enter business description"
            rows={4}
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Entrepreneur */}
        <div>
          <label htmlFor="entrepreneur" className="block text-sm font-medium text-gray-700">
            Entrepreneur
          </label>
          <input
            id="entrepreneur"
            type="text"
            value={entrepreneur}
            onChange={(e) => setEntrepreneur(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Entrepreneur"
          />
        </div>

        {/* Entrepreneur Name */}
        <div>
          <label htmlFor="entrepreneurName" className="block text-sm font-medium text-gray-700">
            Entrepreneur Name
          </label>
          <input
            id="entrepreneurName"
            type="text"
            value={entrepreneurName}
            onChange={(e) => setEntrepreneurName(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Entrepreneur Name"
          />
        </div>

        {/* Organization Name */}
        <div>
          <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">
            Organization Name
          </label>
          <input
            id="organizationName"
            type="text"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Organization Name"
          />
        </div>

        {/* Project Impact Category */}
        <div>
          <label htmlFor="projectImpactCategory" className="block text-sm font-medium text-gray-700">
            Project Impact Category
          </label>
          <input
            id="projectImpactCategory"
            type="text"
            value={projectImpactCategory}
            onChange={(e) => setProjectImpactCategory(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Project Impact Category"
          />
        </div>

        {/* Submission Date */}
        <div>
          <label htmlFor="submissionDate" className="block text-sm font-medium text-gray-700">
            Submission Date
          </label>
          <input
            id="submissionDate"
            type="date"
            value={submissionDate}
            onChange={(e) => setSubmissionDate(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
          />
        </div>

        {/* Finance */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Funding Request</label>
          <input
            type="number"
            value={funding_request}
            onChange={(e) => setFundingRequest(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="Total Funding Request"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Funding Request OPEX</label>
          <input
            type="number"
            value={funding_request_opex}
            onChange={(e) => setFundingRequestOpex(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="OPEX Funding"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Funding Request CAPEX</label>
          <input
            type="number"
            value={funding_request_capex}
            onChange={(e) => setFundingRequestCapex(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
            placeholder="CAPEX Funding"
          />
        </div>
      </div>

      {/* Founders */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Founders</label>
        {founders.map((founder, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input
              type="text"
              value={founder.name}
              onChange={e => handleFounderChange(idx, "name", e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              placeholder="Name"
            />
            <input
              type="text"
              value={founder.expertise}
              onChange={e => handleFounderChange(idx, "expertise", e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              placeholder="Expertise"
            />
            <button type="button" onClick={() => handleRemoveFounder(idx)} className="text-red-500 px-2">✕</button>
          </div>
        ))}
        <button type="button" onClick={handleAddFounder} className="text-blue-500 px-2">+ Add Founder</button>
      </div>

      {/* Business Model Canvas 9 blocks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          ["Key Partners", keyPartners, setKeyPartners],
          ["Key Activities", keyActivities, setKeyActivities],
          ["Key Resources", keyResources, setKeyResources],
          ["Value Propositions", valuePropositions, setValuePropositions],
          ["Customer Relationships", customerRelationships, setCustomerRelationships],
          ["Channels", channels, setChannels],
          ["Customer Segments", customerSegments, setCustomerSegments],
          ["Cost Structure", costStructure, setCostStructure],
          ["Revenue Streams", revenueStreams, setRevenueStreams],
        ].map(([label, arr, setter], i) => (
          <div key={label}>
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            {arr.map((val, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={val}
                  onChange={e => handleArrayChange(setter, arr, idx, e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
                  placeholder={label}
                />
                <button type="button" onClick={() => handleRemoveArrayItem(setter, arr, idx)} className="text-red-500 px-2">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => handleAddArrayItem(setter, arr)} className="text-blue-500 px-2">+ Add</button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          type="submit"
          onClick={onSaveBusinessdetailClicked}
          disabled={!canSave}
          className={`rounded-xl px-4 py-2 text-sm text-white ${
            canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {isLoading ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}